import React, { memo, useEffect } from "react";
import { connect } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import DefaultView from "../components/DefaultView";
import EmailLeftbar from "../components/EmailLeftbar";
import {
  actionGetReceivedMessages,
  actionMarkAddImportant,
  actionMarkAddStarred,
  actionMarkReadMessages,
  actionMarkRemoveImportant,
  actionMarkRemoveStarred,
  actionMarkTrashMessage,
  actionMarkUnreadMessages,
  actionGetMessageDetails,
  actionInboxDetailPageSetIsRead,
  actionRemoveMessageFromTrash,
} from "../store/actions/inboxAction";
import {
  actionMarkTrashSentMessage,
  actionMarkAddImportantSentMessage,
  actionMarkRemoveImportantSentMessage,
  actionGetUnReadMessagesCount,
} from "../store/actions/sentFaxesAction";
import Swal from "sweetalert2";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const ReadEmail = memo(
  ({
    receivedMessages,
    sentMessages,
    actionMarkAddImportant,
    actionMarkRemoveImportant,
    addImportantLoader,
    removeImportantLoader,
    markReadLoader,
    markUnreadLoader,
    markTrashLoader,
    actionMarkReadMessages,
    actionMarkUnreadMessages,
    actionMarkTrashMessage,
    actionMarkTrashSentMessage,
    messageDetailLoader,
    messageDetailData,
    actionGetMessageDetails,
    actionInboxDetailPageSetIsRead,
    actionRemoveMessageFromTrash,
    actionMarkAddImportantSentMessage,
    actionMarkRemoveImportantSentMessage,
    addSentMessageImportantLoader,
    removeSentMessageImportantLoader,
    markSendTrashMessageLoader,
    removeFromTrashLoader,
    actionGetUnReadMessagesCount,
    actionGetReceivedMessages,
  }) => {
    const { type: routeType, id: detailId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Get type and params from location state or derive from route
    const type = location.state?.type || routeType?.toUpperCase() || "INBOX";
    const getMessageParams = location.state?.getMessageParams || {
      offset: 0,
      listType: "NO",
      searchText: "",
    };

    useEffect(() => {
      if (detailId) {
        actionGetMessageDetails(detailId, type);
        if (type == "INBOX") {
          actionInboxDetailPageSetIsRead(detailId);
        }
      }
    }, [detailId]);

    useEffect(() => {
      if (messageDetailData && !messageDetailLoader) {
        const timer = setTimeout(() => {
          actionGetUnReadMessagesCount();
        }, 500);

        return () => clearTimeout(timer);
      }
    }, [messageDetailData, messageDetailLoader]);

    // Initialize tooltips with safe cleanup
    useEffect(() => {
      const tooltipInstances = new Map();

      const initTooltips = () => {
        try {
          const tooltipTriggerList = document.querySelectorAll(
            '[data-bs-toggle="tooltip"]'
          );

          tooltipTriggerList.forEach((tooltipTriggerEl) => {
            try {
              // Check if element still exists and tooltip not already initialized
              if (tooltipTriggerEl && !tooltipInstances.has(tooltipTriggerEl)) {
                const tooltip = new window.bootstrap.Tooltip(tooltipTriggerEl, {
                  trigger: "hover",
                });
                tooltipInstances.set(tooltipTriggerEl, tooltip);
              }
            } catch (error) {
              console.warn("Failed to initialize tooltip:", error);
            }
          });
        } catch (error) {
          console.warn("Tooltip initialization error:", error);
        }
      };

      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(initTooltips, 100);

      return () => {
        clearTimeout(timer);
        // Safe disposal of tooltips
        tooltipInstances.forEach((tooltip, element) => {
          try {
            if (tooltip && typeof tooltip.dispose === "function") {
              tooltip.dispose();
            }
          } catch (error) {
            console.warn("Tooltip disposal error:", error);
          }
        });
        tooltipInstances.clear();
      };
    }, [messageDetailData, messageDetailLoader]);

    /* Button Group for receive message (Inbox) start*/
    const handleMarkReadSelected = () => {
      actionMarkReadMessages({
        recvd_msg_ID: [detailId],
        select_all: "NO",
      });
    };

    const handleMarkUnReadSelected = () => {
      actionMarkUnreadMessages({
        recvd_msg_ID: [detailId],
        select_all: "NO",
      });
    };

    const handleMarkAddImportant = () => {
      actionMarkAddImportant(
        {
          recvd_msg_ID: [detailId],
          select_all: "NO",
        },
        receivedMessages,
        getMessageParams
      );
    };

    const handleMarkAddImportantSent = () => {
      actionMarkAddImportantSentMessage(
        {
          sent_msg_ID: [detailId],
          select_all: "NO",
        },
        receivedMessages,
        getMessageParams
      );
    };

    const handleMarkTrash = () => {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to move this message to trash?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, move to trash",
      }).then((result) => {
        if (result.isConfirmed) {
          actionMarkTrashMessage(
            {
              recvd_msg_ID: [detailId],
            },
            getMessageParams,
            () => navigate("/inbox")
          );
        }
      });
    };
    /* Button Group for receive message (Inbox) end*/

    /* Button Group for send message (Outbox,sent,failed,scheduled) start*/
    const handleMarkRemoveImportantSent = () => {
      actionMarkRemoveImportantSentMessage(
        {
          sent_msg_ID: [detailId],
          select_all: "NO",
        },
        sentMessages,
        getMessageParams
      );
    };

    const handleMarkRemoveImportant = () => {
      actionMarkRemoveImportant(
        {
          recvd_msg_ID: [detailId],
          select_all: "NO",
        },
        sentMessages,
        getMessageParams
      );
    };

    const handleSendMarkTrash = () => {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to move this sent message to trash?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, move to trash",
      }).then((result) => {
        if (result.isConfirmed) {
          actionMarkTrashSentMessage(
            {
              sent_msg_ID: [detailId],
            },
            getMessageParams,
            () => navigate("/sent-faxes")
          );
        }
      });
    };

    /* Button Group for send message (Outbox,sent,failed,scheduled) start*/

    /* Button Group for trash message (Trash) start*/
    const handleRemoveFromTrash = () => {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to permanently delete this message?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          actionRemoveMessageFromTrash(
            {
              trash_msg_ID: [detailId],
            },
            getMessageParams,
            () => navigate("/trash")
          );
        }
      });
    };
    /* Button Group for trash message (Trash) end*/

    const onError = (e) => {};

    function extractRouteAndFileName(url) {
      var result = {
        route: "",
        fileName: "",
      };

      // Split the URL by "/"
      var urlParts = url.split("/");

      // Find the index of "public" in the array
      var publicIndex = urlParts.indexOf("public");

      // If "public" is found, extract the route after it
      if (publicIndex !== -1 && publicIndex < urlParts.length - 1) {
        result.route = urlParts.slice(publicIndex + 1).join("/");

        // Extract the file name
        result.fileName = urlParts[urlParts.length - 1];
      } else {
      }

      return result;
    }

    const FileViewer = ({ fileName }) => {
      const getTrimmedFileName = (fileName) => {
        // Split the file name by '.' to separate the name and extension
        const parts = fileName.split(".");

        // Remove the last part (which is the extension) and join the remaining parts
        const trimmedName = parts.slice(0, -1).join(".");
        const extension = parts[parts.length - 1];

        return { name: trimmedName, extension: extension };
      };

      const { name, extension } = getTrimmedFileName(fileName);

      const renderContent = () => {
        if (["png", "jpeg", "jpg", "gif", "bmp"].includes(extension)) {
          return (
            <>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "150px", backgroundColor: "#f8f9fa" }}
              >
                <i
                  className="fa fa-file-image text-primary"
                  style={{ fontSize: 60 }}
                ></i>
              </div>
              <div className="card-body p-3">
                <div className="d-grid gap-2">
                  <a
                    href={`${BASE_URL}/files/${fileName}`}
                    className="btn btn-sm btn-outline-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fa fa-eye me-1"></i> Preview
                  </a>
                  <a
                    href={`${BASE_URL}/files/${fileName}`}
                    download
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <i className="fa fa-download me-1"></i> Download
                  </a>
                </div>
              </div>
            </>
          );
        } else if (extension === "pdf") {
          return (
            <>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "150px", backgroundColor: "#f8f9fa" }}
              >
                <i
                  className="fa fa-file-pdf text-danger"
                  style={{ fontSize: 60 }}
                ></i>
              </div>
              <div className="card-body p-3">
                <div className="d-grid gap-2">
                  <a
                    href={`${BASE_URL}/files/${fileName}`}
                    className="btn btn-sm btn-outline-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fa fa-eye me-1"></i> Preview
                  </a>
                  <a
                    href={`${BASE_URL}/files/${fileName}`}
                    download
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <i className="fa fa-download me-1"></i> Download
                  </a>
                </div>
              </div>
            </>
          );
        } else if (["csv", "xlsx", "doc", "docx", "xls"].includes(extension)) {
          return (
            <>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "150px", backgroundColor: "#f8f9fa" }}
              >
                <i
                  className="fa fa-file-excel text-success"
                  style={{ fontSize: 60 }}
                ></i>
              </div>
              <div className="card-body p-3">
                <div className="d-grid gap-2">
                  <a
                    href={`${BASE_URL}/files/${fileName}`}
                    className="btn btn-sm btn-outline-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fa fa-eye me-1"></i> Preview
                  </a>
                  <a
                    href={`${BASE_URL}/files/${fileName}`}
                    download
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <i className="fa fa-download me-1"></i> Download
                  </a>
                </div>
              </div>
            </>
          );
        } else {
          // Render content for unknown file types
          return (
            <>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "150px", backgroundColor: "#f8f9fa" }}
              >
                <i
                  className="fa fa-file text-secondary"
                  style={{ fontSize: 60 }}
                ></i>
              </div>
              <div className="card-body p-3">
                <div className="d-grid">
                  <a
                    href={`${BASE_URL}/files/${fileName}`}
                    download
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <i className="fa fa-download me-1"></i> Download
                  </a>
                </div>
              </div>
            </>
          );
        }
      };

      return (
        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6 mb-3">
          <div
            className="card h-100 shadow-sm"
            style={{
              transition: "transform 0.2s, box-shadow 0.2s",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "";
            }}
          >
            {renderContent()}
          </div>
        </div>
      );
    };

    const handleBackToList = () => {
      // Navigate back to appropriate list
      if (type === "INBOX") {
        navigate("/inbox");
      } else if (type === "SENT-FAX") {
        navigate("/sent-faxes");
      } else if (type === "TRASH") {
        navigate("/trash");
      } else {
        navigate(-1);
      }
    };

    return (
      <div className="page-content">
        <div className="container-fluid">
          {/* start page title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 main_heading">Email Inbox</h4>
                <div className="page-title-right">
                  <DefaultView />
                </div>
              </div>
            </div>
          </div>

          {/* end page title */}
          <div className="row">
            <div className="col-12">
              {/* Left sidebar */}
              <EmailLeftbar />
              {/* End Left sidebar */}
              <>
                {/* Right Sidebar */}
                <div className="email-rightbar mb-3">
                  <div className="card">
                    {messageDetailLoader && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: 200,
                        }}
                      >
                        <h5>Loading data..</h5>
                      </div>
                    )}
                    {!messageDetailLoader && messageDetailData && (
                      <>
                        <div className="btn-toolbar gap-2 p-3" role="toolbar">
                          <button
                            onClick={handleBackToList}
                            className="btn btn-primary waves-light waves-effect"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Back"
                          >
                            <i className="fa fa-arrow-left" /> Back
                          </button>
                          <div className="btn-group">
                            {type == "INBOX" && (
                              <>
                                <button
                                  onClick={handleMarkReadSelected}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Mark Read"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {markReadLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fa fa-envelope-open"></i>
                                  )}
                                </button>
                                <button
                                  onClick={handleMarkUnReadSelected}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Mark Unread"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {markUnreadLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fa fa-envelope"></i>
                                  )}
                                </button>
                                <button
                                  onClick={handleMarkAddImportant}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Mark Important"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {addImportantLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fa fa-exclamation-circle" />
                                  )}
                                </button>
                                <button
                                  onClick={handleMarkRemoveImportant}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Mark Not Important"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {removeImportantLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fa fa-times" />
                                  )}
                                </button>
                                <button
                                  onClick={handleMarkTrash}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Move to trash"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {markTrashLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="far fa-trash-alt" />
                                  )}
                                </button>
                              </>
                            )}
                            {type == "SENT-FAX" && (
                              <>
                                <button
                                  onClick={handleMarkAddImportantSent}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Mark Important"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {addSentMessageImportantLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fa fa-exclamation-circle" />
                                  )}
                                </button>
                                <button
                                  onClick={handleMarkRemoveImportantSent}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Mark Not Important"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {removeSentMessageImportantLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fa fa-times" />
                                  )}
                                </button>
                                <button
                                  onClick={handleSendMarkTrash}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Move to trash"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {markSendTrashMessageLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="far fa-trash-alt" />
                                  )}
                                </button>
                              </>
                            )}
                            {type == "TRASH" && (
                              <>
                                <button
                                  onClick={handleRemoveFromTrash}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Delete Permanently"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {removeFromTrashLoader ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="far fa-trash-alt" />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="d-flex align-items-top mb-4 justify-content-between">
                            <div className="name-mail-wrap">
                              <div className="flex-grow-1">
                                <h5 className="font-size-14 mb-0">{`Message From:- ${
                                  messageDetailData.msg &&
                                  messageDetailData.msg.msg_senderName
                                }`}</h5>
                                <small className="text-muted">{`From Number:- ${
                                  messageDetailData.msg &&
                                  messageDetailData.msg.msg_fromNumber
                                }`}</small>

                                <small className="text-muted">{`To Number:- ${
                                  messageDetailData.msg &&
                                  messageDetailData.msg.msg_toNumber
                                }`}</small>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            {messageDetailData &&
                              messageDetailData.doc &&
                              messageDetailData.doc.map((data, index) => (
                                <FileViewer
                                  fileName={data.doc_direction}
                                  key={index}
                                />
                              ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* card */}
              </>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => {
  return {
    getReceivedMessagesLoader: state.inbox.getReceivedMessagesLoader,
    receivedMessages: state.inbox.receivedMessages,
    receivedMessagesCount: state.inbox.receivedMessagesCount,
    addImportantLoader: state.inbox.addImportantLoader,
    removeImportantLoader: state.inbox.removeImportantLoader,
    markReadLoader: state.inbox.markReadLoader,
    markUnreadLoader: state.inbox.markUnreadLoader,
    markTrashLoader: state.inbox.markTrashLoader,
    messageDetailLoader: state.inbox.messageDetailLoader,
    messageDetailData: state.inbox.messageDetailData,
    sentMessages: state.sentFax.sentMessages,
    addSentMessageImportantLoader: state.sentFax.addSentMessageImportantLoader,
    removeSentMessageImportantLoader:
      state.sentFax.removeSentMessageImportantLoader,
    markSendTrashMessageLoader: state.sentFax.markSendTrashMessageLoader,
    removeFromTrashLoader: state.inbox.removeFromTrashLoader,
  };
};

export default connect(mapStateToProps, {
  actionGetReceivedMessages,
  actionMarkAddImportant,
  actionMarkRemoveImportant,
  actionMarkReadMessages,
  actionMarkUnreadMessages,
  actionMarkAddStarred,
  actionMarkRemoveStarred,
  actionMarkTrashMessage,
  actionMarkTrashSentMessage,
  actionGetMessageDetails,
  actionInboxDetailPageSetIsRead,
  actionRemoveMessageFromTrash,
  actionMarkAddImportantSentMessage,
  actionMarkRemoveImportantSentMessage,
  actionGetUnReadMessagesCount,
})(ReadEmail);
